import React, { useEffect, useState } from 'react';
import { BookOpen, Plus, Pencil, Trash, X, ExternalLink, RefreshCw, FileText } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const SCHEDULE_YEAR = 2026;

const isoFromDisplayDate = (displayDate: string): string => {
  const parsed = new Date(`${displayDate} ${SCHEDULE_YEAR}`);
  if (Number.isNaN(parsed.getTime())) return '';
  return format(parsed, 'yyyy-MM-dd');
};

const dateFromIso = (iso: string): Date | null => {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};
import {
  fetchReadingSchedule,
  createReadingItem,
  updateReadingItem,
  deleteReadingItem,
  ReadingItem,
  ReadingItemInput,
  fetchReadingPageContent,
  updateReadingPageContent,
  ReadingPageContent,
  READING_LOCATIONS,
} from '../lib/readingSchedule';

const emptyPageContent: ReadingPageContent = {
  header_title: '',
  header_description: '',
  footer_title: '',
  footer_description: '',
};

const emptyForm: ReadingItemInput = {
  sort_order: 0,
  reading_date: null,
  date: '',
  month: '',
  location: 'Small group location',
  text: '',
  reading_prompt: '',
  is_off: false,
  off_reason: '',
  esv_url: '',
  podcast_url: '',
  podcast_label: '',
  video_urls: [],
  video_labels: [],
  additional_urls: [],
  additional_labels: [],
};

type LinkCategory = 'podcast' | 'video' | 'additional';

interface LinkRow {
  url: string;
  label: string;
  category: LinkCategory;
}

const zipLinks = (urls: string[], labels: string[], category: LinkCategory): LinkRow[] =>
  urls.map((url, i) => ({ url, label: labels[i] ?? '', category }));

const normalizeUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  return `https://${trimmed}`;
};

const cleanRows = (rows: LinkRow[]): LinkRow[] =>
  rows
    .map((row) => ({ url: normalizeUrl(row.url), label: row.label.trim(), category: row.category }))
    .filter((row) => row.url.length > 0);

const LinkRowsEditor: React.FC<{
  label: string;
  urlPlaceholder: string;
  namePlaceholder: string;
  addLabel: string;
  rows: LinkRow[];
  onChange: (rows: LinkRow[]) => void;
}> = ({ label, urlPlaceholder, namePlaceholder, addLabel, rows, onChange }) => {
  const update = (index: number, patch: Partial<LinkRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const remove = (index: number) => onChange(rows.filter((_, i) => i !== index));
  const add = () => onChange([...rows, { url: '', label: '', category: 'additional' }]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="text"
                value={row.url}
                onChange={(e) => update(index, { url: e.target.value })}
                placeholder={urlPlaceholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <input
                type="text"
                value={row.label}
                onChange={(e) => update(index, { label: e.target.value })}
                placeholder={namePlaceholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => remove(index)}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
              title="Remove link"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
      >
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
      <p className="text-xs text-gray-500 mt-1">The name is optional. Leave it blank to use the default label.</p>
    </div>
  );
};

const AdminReading: React.FC = () => {
  const [items, setItems] = useState<ReadingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ReadingItemInput>(emptyForm);
  const [dateValue, setDateValue] = useState('');
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [showPageModal, setShowPageModal] = useState(false);
  const [savingPage, setSavingPage] = useState(false);
  const [pageForm, setPageForm] = useState<ReadingPageContent>(emptyPageContent);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await fetchReadingSchedule());
    } catch {
      toast.error('Failed to load reading schedule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openPageEditor = async () => {
    try {
      const content = await fetchReadingPageContent();
      setPageForm(content ?? emptyPageContent);
      setShowPageModal(true);
    } catch {
      toast.error('Failed to load page text');
    }
  };

  const handlePageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingPage(true);
    try {
      await updateReadingPageContent({
        header_title: pageForm.header_title.trim(),
        header_description: pageForm.header_description.trim(),
        footer_title: pageForm.footer_title.trim(),
        footer_description: pageForm.footer_description.trim(),
      });
      toast.success('Page text updated');
      setShowPageModal(false);
    } catch {
      toast.error('Failed to save page text');
    } finally {
      setSavingPage(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setDateValue('');
    setLinks([]);
    setShowModal(true);
  };

  const openEdit = (item: ReadingItem) => {
    setEditingId(item.id);
    setForm({
      sort_order: item.sort_order,
      reading_date: item.reading_date,
      date: item.date,
      month: item.month,
      location: item.location,
      text: item.text,
      reading_prompt: item.reading_prompt ?? '',
      is_off: item.is_off,
      off_reason: item.off_reason ?? '',
      esv_url: item.esv_url ?? '',
      podcast_url: item.podcast_url ?? '',
      podcast_label: item.podcast_label ?? '',
      video_urls: item.video_urls,
      video_labels: item.video_labels,
      additional_urls: item.additional_urls,
      additional_labels: item.additional_labels,
    });
    setDateValue(item.reading_date ?? isoFromDisplayDate(item.date));
    setLinks([
      ...(item.podcast_url
        ? [{ url: item.podcast_url, label: item.podcast_label ?? '', category: 'podcast' as const }]
        : []),
      ...zipLinks(item.video_urls, item.video_labels, 'video'),
      ...zipLinks(item.additional_urls, item.additional_labels, 'additional'),
    ]);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pickedDate = dateFromIso(dateValue);
    if (!pickedDate || !form.text.trim()) {
      toast.error('Date and title are required');
      return;
    }
    const cleanedLinks = cleanRows(links);
    const podcastRow = cleanedLinks.find((r) => r.category === 'podcast');
    const videoRows = cleanedLinks.filter((r) => r.category === 'video');
    const additionalRows = cleanedLinks.filter((r) => r.category === 'additional');
    const payload: ReadingItemInput = {
      sort_order: form.sort_order,
      reading_date: format(pickedDate, 'yyyy-MM-dd'),
      date: format(pickedDate, 'MMM d'),
      month: format(pickedDate, 'MMMM'),
      location: form.location,
      text: form.text.trim(),
      reading_prompt: form.is_off ? null : form.reading_prompt?.trim() || null,
      is_off: form.is_off,
      off_reason: form.is_off ? form.off_reason?.trim() || null : null,
      esv_url: form.is_off ? null : normalizeUrl(form.esv_url ?? '') || null,
      podcast_url: podcastRow?.url ?? null,
      podcast_label: podcastRow?.label || null,
      video_urls: videoRows.map((r) => r.url),
      video_labels: videoRows.map((r) => r.label),
      additional_urls: additionalRows.map((r) => r.url),
      additional_labels: additionalRows.map((r) => r.label),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateReadingItem(editingId, payload);
        toast.success('Reading updated');
      } else {
        await createReadingItem(payload);
        toast.success('Reading added');
      }
      setShowModal(false);
      await load();
    } catch {
      toast.error('Failed to save reading');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: ReadingItem) => {
    if (!window.confirm(`Delete "${item.date} - ${item.text}"?`)) return;
    try {
      await deleteReadingItem(item.id);
      toast.success('Reading deleted');
      await load();
    } catch {
      toast.error('Failed to delete reading');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <BookOpen className="w-6 sm:w-7 h-6 sm:h-7 text-gray-900" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reading Schedule</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Manage the reading plan shown on the public Reading page
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openPageEditor}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2 whitespace-nowrap"
          >
            <FileText className="w-5 h-5" />
            <span>Edit Page Text</span>
          </button>
          <button
            onClick={openAdd}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 whitespace-nowrap"
          >
            <Plus className="w-5 h-5" />
            <span>Add Reading</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No readings yet. Add the first one to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="divide-y divide-gray-100">
            {items.map((item) => (
              <div key={item.id} className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-500 whitespace-nowrap">{item.date}</span>
                    <h3 className="text-base font-semibold text-gray-900 break-words">{item.text}</h3>
                    {item.is_off && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                        Off week
                      </span>
                    )}
                  </div>
                  {item.reading_prompt && (
                    <p className="text-sm text-gray-600">{item.reading_prompt}</p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                    {item.podcast_url && (
                      <span className="inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Podcast
                      </span>
                    )}
                    {item.video_urls.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> {item.video_urls.length} video{item.video_urls.length > 1 ? 's' : ''}
                      </span>
                    )}
                    {item.additional_urls.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> {item.additional_urls.length} resource{item.additional_urls.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEdit(item)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showPageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
              <h3 className="text-lg font-semibold text-gray-900">Edit Page Text</h3>
              <button
                onClick={() => setShowPageModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handlePageSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Top heading</label>
                <input
                  type="text"
                  value={pageForm.header_title}
                  onChange={(e) => setPageForm({ ...pageForm, header_title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Top description</label>
                <textarea
                  value={pageForm.header_description}
                  onChange={(e) => setPageForm({ ...pageForm, header_description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bottom heading</label>
                <input
                  type="text"
                  value={pageForm.footer_title}
                  onChange={(e) => setPageForm({ ...pageForm, footer_title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bottom description</label>
                <textarea
                  value={pageForm.footer_description}
                  onChange={(e) => setPageForm({ ...pageForm, footer_description: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Leave a blank line between paragraphs to keep them separated.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPageModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingPage}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-60"
                >
                  {savingPage ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit Reading' : 'Add Reading'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={dateValue}
                  onChange={(e) => setDateValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                {dateValue && dateFromIso(dateValue) && (
                  <p className="text-xs text-gray-500 mt-1">
                    Shows as {format(dateFromIso(dateValue) as Date, 'MMM d')} under {format(dateFromIso(dateValue) as Date, 'MMMM')}.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting location</label>
                <div className="space-y-2">
                  {READING_LOCATIONS.map((loc) => (
                    <label key={loc} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="location"
                        value={loc}
                        checked={form.location === loc}
                        onChange={() => setForm({ ...form, location: loc })}
                        className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{loc}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder="e.g. John 3 or Off Week - No Meeting"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_off}
                  onChange={(e) => setForm({ ...form, is_off: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">This is an off week (no meeting)</span>
              </label>

              {form.is_off ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Off week reason</label>
                  <input
                    type="text"
                    value={form.off_reason ?? ''}
                    onChange={(e) => setForm({ ...form, off_reason: e.target.value })}
                    placeholder="e.g. Labor Day"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reading prompt</label>
                  <input
                    type="text"
                    value={form.reading_prompt ?? ''}
                    onChange={(e) => setForm({ ...form, reading_prompt: e.target.value })}
                    placeholder="e.g. Read John 3 before this week's meeting"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              )}

              {!form.is_off && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ESV reading link</label>
                  <input
                    type="text"
                    value={form.esv_url ?? ''}
                    onChange={(e) => setForm({ ...form, esv_url: e.target.value })}
                    placeholder="https://www.esv.org/John+3/"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional. Paste the esv.org link for this week's passage. Leave blank to hide the Read button.
                  </p>
                </div>
              )}

              <LinkRowsEditor
                label="Links"
                urlPlaceholder="https://..."
                namePlaceholder="Link name (optional)"
                addLabel="Add link"
                rows={links}
                onChange={setLinks}
              />

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Reading'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReading;